import { EmbedElementSaveModel } from "client/Api/Hub/common.composer";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef
} from "react";
import { useBuilderEditor } from "../../useBuilderEditor";
import styles from "./Embed.module.less";
import { UserHtml } from "client/Features/UserHtml/UserHtml";
import { ElementLayout } from "../ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "../Element";
import { ValidationError } from "client/Components";
import { Guid } from "client_legacy/ndaw/guid";
import { getClientId, hasClientId } from "client/Misc/clientId";

export type EmbedProps = {
	attachable: IValueInputAttachable<EmbedElementSaveModel>;
	genericHandlers: ElementGenericActionHandlers;
	ref: Ref<ElementGenericHandle>;
};

export const Embed = (props: EmbedProps): ReactElement => {
	const {
		attachable,
		genericHandlers,
		ref
	} = props;

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const {
		activate
	} = useBuilderEditor();

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const trackingId: string = attachable.reducer
		.path;

	const { code } = attachable.value;

	const editHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<string> = await onOpenEmbedInputDialog({
				code: attachable.value.code
			});

			if (result.wasSuccessful) {
				attachable.for("code")
					.reducer.setValue(result.result);
				attachable.for("code")
					.validation.triggerInputFinished();
			}
		},
		[attachable, onOpenEmbedInputDialog]
	);

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(attachable.value) && getClientId(attachable.value) === clientId) {
				ensureNotNil(rootRef.current)
					.focus();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[attachable.value]
	);

	return (
		<ElementLayout
			hasError={!attachable.validation.isValid()}
			trackingId={attachable.reducer.path}
			ref={rootRef}
		>
			<ElementLayout.ActionsMenu trackingId="element-actions">
				<ElementLayout.AddElementMenuItem
					onAddNewElementToColumn={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
				/>
				{onMoveUp && (
					<ElementLayout.MoveUpMenuItem
						onClick={onMoveUp}
					/>
				)}
				{onMoveDown && (
					<ElementLayout.MoveDownMenuItem
						onClick={onMoveDown}
					/>
				)}
				<ElementLayout.EditMenuItem
					onClick={editHandler}
				/>
				<ElementLayout.DuplicateMenuItem
					onClick={onDuplicate}
				/>
				<ElementLayout.DeleteMenuItem
					onClick={onDelete}
				/>
			</ElementLayout.ActionsMenu>
			<div
				{...setTrackingId(trackingId, "__embed")}
				onClick={activate}
				className={styles.embed}
			>
				<UserHtml
					trackingId="user-html"
					html={code}
				/>
			</div>
			<ValidationError {...attachable.for("code").attachToValidationError()} />
		</ElementLayout>
	);
};
Embed.displayName = "Embed";
